import { SmartTeamsAppUserGroup } from '@nsc/smartteams-shared/db/SmartTeamsAppUserGroup.js';

/**
 * Whose tickets the Colleagues view may show to this user, by partner / internal type.
 *
 *   - an INTERNAL field engineer sees internal colleagues only — never a partner's tickets;
 *   - a PARTNER field engineer sees no colleagues at all — only their own tickets, which the plain Tickets list
 *     already gives them (partners must not see each other's);
 *   - an NSC admin oversees everyone;
 *   - a partner manager sees the partner engineers of their company, never internal ones;
 *   - anyone else sees none.
 *
 * Fails closed: a field engineer whose engineer record cannot be found is shown nothing, rather than guessed at.
 * (A record with no `isPartner` at all is internal — the convention everywhere: blank means internal.)
 *
 * Pure, so the rules can be tested without a database.
 */
export type ColleagueScope = 'all' | 'internal' | 'partner' | 'none';

export function colleagueScope(input: {
  userGroup: SmartTeamsAppUserGroup | null | undefined;
  /** The viewer's own engineer record: its `isPartner`. Only read for a field engineer. */
  feIsPartner: boolean | null | undefined;
  /** Whether the viewer's engineer record was found at all. */
  hasFeRecord: boolean;
}): ColleagueScope {
  switch (input.userGroup) {
    case SmartTeamsAppUserGroup.Admin:
      return 'all';
    case SmartTeamsAppUserGroup.PartnerManager:
      return 'partner';
    case SmartTeamsAppUserGroup.FieldEngineer:
      if (!input.hasFeRecord) return 'none';
      return input.feIsPartner === true ? 'none' : 'internal';
    default:
      return 'none';
  }
}

/** Mongo condition for the engineers a scope may list. Not meant for `none`, which lists nobody. */
export function engineerTypeFilter(scope: ColleagueScope): Record<string, unknown> {
  if (scope === 'internal') return { isPartner: { $ne: true } };
  if (scope === 'partner') return { isPartner: true };
  return {};
}
